import { getChainTime, } from "@bracket/tenderly/time";
import * as G from "@globals";
import { expected_epoch, lock_date, unlock_time, } from "@store/commons";
import type { IBracketStore, IdentifiableVaultWithState, LoadedVault, VaultMeta, } from "@types";
import { assetToUsd, } from "@utils";
import { DateTime, } from "luxon";
import { getLoadedVault, } from "@vault/resolvers";
import { getVaultAPY, } from "../getVaultAPY";
import { multiCaller, contract, getTokenDefinition, } from "@bracket/chainography/contracts";
import { wagmiConfig as config, } from "@wagmiconfig";
import { getAggregateError, } from "@bracket/catch";



/**
 * Retrieves metadata for a vault including supply, prices, rates, and performance metrics
 * @param identifier - The vault identifier containing address and optional chainId
 * @returns Promise resolving to VaultMeta containing comprehensive vault metadata
 * @throws Error if no wallet address is set or if vault data cannot be retrieved
 */
export const getVaultMeta = async ( store: IBracketStore, identifier: IdentifiableVaultWithState, ): Promise<VaultMeta> => {

	try {

		const VAULT = await getLoadedVault(store,identifier);
		const { address, } = G.wallet.getBracketWallet();
		
		if(!address){
			throw new Error("getVaultMeta called whilst no address is set")
		}

		if(!["ACTIVE", "PREPARED"].includes(VAULT.status)) {
			return initialData;
		}

		const ver = Number.parseInt(VAULT.version || "1", 10);
		let result:VaultMeta = await getBaseData(VAULT);

		for(let i = 0; i < ver; i++) {
			result = await extendedFunctions[i](result);
		}
		return result;
	}
	catch( err: unknown ) {

		console.warn(`failed getting vault meta for ${identifier.symbol}`, err);
		throw new Error(`failed getting vault meta for ${identifier.symbol}`);
	}
}


const initialData: VaultMeta = {
	apy: 0,
	assets_per_share: 0,
	btc_price: 0,
	brkt_eth_rate: 0,
	current_epoch: 0,
	current_nav: 0,
	current_value: {
		brkt: "0",
		usd: 0,
	},
	epoch_actual: 0,
	epoch_delayed: false,
	eth_price: 0,
	lock_message: null,
	lock_forward: null,
	lock_time: DateTime.fromMillis(0),
	locked: false,
	nav_launch: 0,
	perma_lock: false,
	return: -999,
	share_price: -999,
	shares: 0,
	time_since_launch: 0,
	total_supply: 0n,
	tvl: {
		brkt: "0",
		usd: 0,
	},
	unlock_time: DateTime.fromMillis(0)
}


const getBaseData = async ( vault: LoadedVault, ): Promise<VaultMeta> => {

	try {

		const {
			denomination,
			denomination: {
				decimals,
			},
			symbol,
		} = vault;


		const closed = symbol === "BBOR1";

		const VaultContractName = vault.version === null ? "BracketVault" : "BracketVaultV2";
		const [ [ brkt_rate, total_supply, nav, totalActiveShares, epoch_actual, contractLocked, eth_price_bi, btc_price_bi, totalDeposits, totalQueuedDeposits, totalShares, ], errors ] = await multiCaller<[ bigint, bigint, bigint, bigint, number, boolean, number, number, bigint, bigint, bigint ]>(config, [
			contract("BrktETH", "getBracketRate"),
			contract(VaultContractName, "totalSupply", vault.address),
			contract(VaultContractName, "assetsPerShare", vault.address),
			vault.version ? contract(VaultContractName, "totalShares", vault.address) : contract(VaultContractName, "totalActiveShares", vault.address),
			contract(VaultContractName, "epoch", vault.address),
			contract(VaultContractName, "isVaultLocked", vault.address),
			contract("Pricing", "getLatestPrice", getTokenDefinition("ETH_USD").address),
			contract("Pricing", "getLatestPrice", getTokenDefinition("BTC_USD").address),
			contract(VaultContractName, "totalDeposits", vault.address),
			vault.version ? contract(VaultContractName, "totalQueuedDeposits", vault.address) : contract(VaultContractName, "totalDeposits", vault.address),
			vault.version ? contract(VaultContractName, "totalShares", vault.address) : contract(VaultContractName, "totalActiveShares", vault.address),
		]);

		const vaultAPY = await getVaultAPY(vault);

		if(errors) {
			throw getAggregateError(errors, `getVaultMeta::getBaseData failed for vault ${vault.symbol}`);
		}

		if(typeof totalShares !== "bigint" || typeof nav !== "bigint" || typeof totalDeposits !== "bigint" || typeof totalQueuedDeposits !== "bigint") {
			console.log("YYYYK-GO>", errors, totalShares, nav, totalDeposits, totalQueuedDeposits);
			console.log("K-GO>", totalShares, nav, totalDeposits, totalQueuedDeposits);
			console.log("---");
		}

		const shares_as_assets = BigInt(Math.floor(Number(totalShares * nav) / (10**decimals)));
		const current_value = totalDeposits + totalQueuedDeposits + shares_as_assets;
		const eth_price = Number(eth_price_bi) / (10**getTokenDefinition("ETH_USD").decimals);
		const btc_price = Number(btc_price_bi) / (10**getTokenDefinition("BTC_USD").decimals);

		// const current_value = BigInt(Math.floor((Number(totalActiveShares) / (10**decimals)) * Number(nav)));
		// const current_value = strategyTVL.brkt;
		const current_ms = await getChainTime();
		const time_since_launch = DateTime.fromISO(vault.nav.updated as unknown as string).diffNow().seconds;
		const nav_launch = DateTime.fromISO(vault.nav.updated as unknown as string).toSeconds();
		const lock_time = lock_date(vault);
		const expected = expected_epoch(vault, current_ms);
		const locked = contractLocked; //(vault.epoch === 0 ? false : vault.epoch < expected);
		const lock_message = symbol === "BBOR1" ? `The vault is permanently closed, and all assets have transitioned to ETH+ Alpha Vault (brETH) since August 20th 18:00 UTC August 20, 2025 at 2:00 PM EDT.` : null;

		const { hostname, protocol, } = globalThis.location as { hostname: string, protocol: "http:" | "https:" };
		// const lock_time = DateTime.now().minus({ days: 1, })
		
		const meta: VaultMeta = {
			apy: vaultAPY,
			assets_per_share: Number(nav) / (10**decimals),
			brkt_eth_rate: brkt_rate,
			current_epoch: vault.nav.epoch,
			current_nav: Number(vault.nav.nav.toString()) / (10**decimals),
			current_value: {
				brkt: current_value.toString(),
				usd: await assetToUsd(current_value, denomination),
			},
			epoch_actual,
			epoch_delayed: epoch_actual > 0 && epoch_actual < expected,
			eth_price: eth_price * getTokenDefinition("ETH_USD").decimals,
			btc_price: btc_price * getTokenDefinition("BTC_USD").decimals,
			lock_forward: symbol === "BBOR1" ? `/invest/brETH` : null,
			lock_time,
			locked,
			nav_launch,
			perma_lock: symbol === "BBOR1",
			lock_message,
			return: -999,
			// share_price: closed ? 0 : await assetToUsd(nav, denomination),
			share_price: await assetToUsd(nav, denomination),
			shares: Number(totalActiveShares) / (10**decimals),
			time_since_launch,
			total_supply,
			tvl: {
				brkt: current_value.toString(),
				usd: await assetToUsd(current_value, denomination),
			},
			unlock_time: unlock_time(vault),
		};

		return meta;

	}
	catch( err: unknown ) {

		throw getAggregateError(err, `getVaultMeta::getBaseData failed for vault ${vault.symbol}`);
	}
}

const getVaultMetaV1 = async ( meta: VaultMeta, ): Promise<VaultMeta> => {
	return meta;
}


const getVaultMetaV2 = async ( meta: VaultMeta, ): Promise<VaultMeta> => {
	return meta;
}


const extendedFunctions = [
	getVaultMetaV1,
	getVaultMetaV2,
]
